import { alpha, createTheme, lighten, darken } from '@mui/material';
import { themeColors, darkPalette, commonComponents, gradients, shadows } from '../colors';

const colors = {
  gradients: gradients,
  shadows: shadows,
  alpha: {
    white: {
      5: alpha('#ffffff', 0.02),
      10: alpha('#ffffff', 0.1),
      30: alpha('#ffffff', 0.3),
      50: alpha('#ffffff', 0.5),
      70: alpha('#ffffff', 0.7),
      100: '#ffffff'
    },
    black: {
      5: alpha('#000000', 0.02),
      10: alpha('#000000', 0.1),
      30: alpha('#000000', 0.3),
      50: alpha('#000000', 0.5),
      70: alpha('#000000', 0.7),
      100: '#000000'
    }
  },
  secondary: {
    lighter: alpha(themeColors.secondary, 0.1),
    light: lighten(themeColors.secondary, 0.25),
    main: themeColors.secondary,
    dark: darken(themeColors.secondary, 0.2)
  },
  primary: {
    lighter: alpha(themeColors.primary, 0.1),
    light: lighten(themeColors.primary, 0.3),
    main: themeColors.primary,
    dark: darken(themeColors.primary, 0.2)
  },
  success: {
    lighter: alpha(themeColors.success, 0.1),
    light: lighten(themeColors.success, 0.3),
    main: themeColors.success,
    dark: darken(themeColors.success, 0.2)
  },
  warning: {
    lighter: alpha(themeColors.warning, 0.1),
    light: lighten(themeColors.warning, 0.3),
    main: themeColors.warning,
    dark: darken(themeColors.warning, 0.2)
  },
  error: {
    lighter: alpha(themeColors.error, 0.1),
    light: lighten(themeColors.error, 0.3),
    main: themeColors.error,
    dark: darken(themeColors.error, 0.2)
  },
  info: {
    lighter: alpha(themeColors.info, 0.1),
    light: lighten(themeColors.info, 0.3),
    main: themeColors.info,
    dark: darken(themeColors.info, 0.2)
  }
};

export const DarkTheme = createTheme({
  colors: {
    gradients: colors.gradients,
    shadows: colors.shadows,
    alpha: colors.alpha
  },
  general: {
    reactFrameworkColor: '#00D8FF',
    borderRadiusSm: '6px',
    borderRadius: '10px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px'
  },
  sidebar: {
    background: darkPalette.background.paper,
    dividerBg: '#1e2a3a',
    menuItemColor: darkPalette.text.secondary,
    menuItemColorActive: darkPalette.primary.main,
    menuItemBg: darkPalette.background.paper,
    menuItemBgActive: alpha('#ffffff', 0.08),
    menuItemIconColor: darkPalette.text.secondary,
    menuItemIconColorActive: darkPalette.primary.main,
    menuItemHeadingColor: darkPalette.text.primary,
    boxShadow: '2px 0 3px rgba(0, 0, 0, .5), 1px 0 1px rgba(0, 0, 0, 0.5)',
    width: '240px'
  },
  header: {
    height: '80px',
    background: darkPalette.background.paper,
    boxShadow: '0px 2px 3px rgba(0, 0, 0, .5), 0px 1px 1px rgba(0, 0, 0, 0.5)'
  },
  spacing: 9,
  palette: darkPalette,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1840
    }
  },
  components: {
    ...commonComponents,
    MuiCssBaseline: {
      styleOverrides: {
        ...commonComponents.MuiCssBaseline.styleOverrides,
        body: {
          ...commonComponents.MuiCssBaseline.styleOverrides.body,
          backgroundColor: darkPalette.background.main,
          color: darkPalette.text.primary
        },
        '#nprogress .bar': {
          background: alpha(darkPalette.primary.main, 0.3)
        },
        '#nprogress .spinner-icon': {
          borderTopColor: alpha(darkPalette.primary.main, 0.3),
          borderLeftColor: alpha(darkPalette.primary.main, 0.3)
        },
        '#nprogress .peg': {
          boxShadow: `0 0 15px ${alpha(darkPalette.primary.main, 0.3)}, 0 0 8px ${darkPalette.primary.light}`
        },
        code: {
          background: alpha(darkPalette.info.main, 0.1),
          color: darkPalette.info.light,
          borderRadius: 4,
          padding: 4
        },
        '@keyframes pulse': {
          '0%': { transform: 'scale(.75)' },
          '20%': { transform: 'scale(1.1)' },
          '40%': { transform: 'scale(.75)' },
          '60%': { transform: 'scale(1.05)' },
          '80%': { transform: 'scale(.75)' },
          '100%': { transform: 'scale(.75)' }
        },
        '@keyframes ripple': {
          '0%': { transform: 'scale(.8)', opacity: 1 },
          '100%': { transform: 'scale(2.8)', opacity: 0 }
        },
        '@keyframes float': {
          '0%': { transform: 'translate(0%, 0%)' },
          '100%': { transform: 'translate(3%, 3%)' }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        ...commonComponents.MuiPaper.styleOverrides,
        root: {
          ...commonComponents.MuiPaper.styleOverrides.root,
          backgroundColor: darkPalette.background.paper,
          backgroundImage: 'none',
          borderRadius: 0,
          border: '1px solid rgba(255, 255, 255, 0.08)'
        },
        elevation0: { boxShadow: 'none' },
        elevation1: { boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)' },
        elevation2: { boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)' }
      }
    },
    MuiCard: {
      defaultProps: {
        elevation: 2
      },
      styleOverrides: {
        root: {
          backgroundColor: darkPalette.background.paper,
          backgroundImage: 'none',
          borderRadius: 16,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
            boxShadow: '0 6px 16px rgba(255, 255, 255, 0.15)',
            transform: 'translateY(-1px)'
          }
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        ...commonComponents.MuiCardHeader.styleOverrides,
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '24px 24px 16px'
        }
      }
    },
    MuiCardContent: {
      styleOverrides: commonComponents.MuiCardContent.styleOverrides
    },
    MuiListItemButton: {
      styleOverrides: {
        ...commonComponents.MuiListItemButton.styleOverrides,
        root: {
          ...commonComponents.MuiListItemButton.styleOverrides.root,
          '&:hover': {
            backgroundColor: darkPalette.action.hover
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(85, 105, 255, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(85, 105, 255, 0.24)'
            }
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        ...commonComponents.MuiDivider.styleOverrides,
        root: {
          ...commonComponents.MuiDivider.styleOverrides.root,
          background: darkPalette.divider
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: darkPalette.background.paper,
          color: darkPalette.text.primary,
          backgroundImage: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: darkPalette.background.paper,
          color: darkPalette.text.primary,
          borderRight: '1px solid rgba(255, 255, 255, 0.08)'
        }
      }
    },
    MuiButton: {
      ...commonComponents.MuiButton,
      styleOverrides: {
        ...commonComponents.MuiButton.styleOverrides,
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
          }
        },
        containedSecondary: {
          backgroundColor: colors.secondary.main,
          color: colors.alpha.white[100],
          border: `1px solid ${alpha(darkPalette.common.white, 0.3)}`
        },
        outlinedSecondary: {
          backgroundColor: colors.alpha.white[100],
          '&:hover, &.MuiSelected': {
            backgroundColor: alpha(darkPalette.common.white, 0.05),
            color: colors.alpha.black[100]
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 8,
          '& .MuiTouchRipple-root': { borderRadius: 8 }
        },
        sizeSmall: { padding: 4 }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiInputAdornment-positionEnd.MuiInputAdornment-outlined': {
            paddingRight: 6
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(darkPalette.common.white, 0.5)
          },
          '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: darkPalette.primary.main
          }
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        colorPrimary: {
          fontWeight: 'bold',
          lineHeight: '40px',
          fontSize: 13,
          background: alpha(darkPalette.common.white, 0.05),
          color: alpha(darkPalette.common.white, 0.7)
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        colorSecondary: {
          background: alpha(darkPalette.common.white, 0.05),
          color: darkPalette.common.white,
          '&:hover': {
            background: alpha(darkPalette.common.white, 0.1)
          }
        },
        deleteIcon: {
          color: lighten(darkPalette.error.main, 0.3),
          '&:hover': {
            color: darkPalette.error.main
          }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        ...commonComponents.MuiAvatar.styleOverrides,
        colorDefault: {
          background: alpha(darkPalette.common.white, 0.3),
          color: darkPalette.common.white
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        page: {
          fontSize: 13,
          fontWeight: 'bold',
          transition: 'all .2s'
        },
        textPrimary: {
          '&.Mui-selected': { boxShadow: colors.shadows.primary },
          '&.MuiButtonBase-root:hover': { background: alpha(darkPalette.common.white, 0.05) },
          '&.Mui-selected.MuiButtonBase-root:hover': { background: darkPalette.primary.main }
        }
      }
    },
    MuiToggleButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          color: darkPalette.primary.main,
          background: darkPalette.background.paper,
          transition: 'all .2s',
          '&:hover, &.Mui-selected, &.Mui-selected:hover': {
            color: darkPalette.common.white,
            background: darkPalette.primary.main
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
          '& .MuiListItem-button': {
            transition: 'all .2s',
            '& > .MuiSvgIcon-root': { minWidth: 34 },
            '& .MuiTouchRipple-root': { opacity: 0.2 }
          },
          '& .MuiListItem-root.MuiButtonBase-root.Mui-selected': {
            backgroundColor: alpha(darkPalette.primary.main, 0.16)
          },
          '& .MuiMenuItem-root.MuiButtonBase-root:active': {
            backgroundColor: alpha(darkPalette.primary.main, 0.16)
          }
        },
        padding: {
          padding: '12px',
          '& .MuiListItem-button': {
            borderRadius: 6,
            margin: '1px 0'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: { height: 38, minHeight: 38, overflow: 'visible' },
        indicator: {
          height: 38,
          minHeight: 38,
          borderRadius: 6,
          border: `1px solid ${darkPalette.primary.dark}`,
          boxShadow: `0px 2px 10px ${alpha(darkPalette.primary.main, 0.5)}`
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: 0,
          height: 38,
          minHeight: 38,
          borderRadius: 6,
          transition: 'color .2s',
          textTransform: 'capitalize',
          '&.MuiButtonBase-root': {
            minWidth: 'auto',
            paddingLeft: 20,
            paddingRight: 20,
            marginRight: 4
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            color: darkPalette.common.white,
            zIndex: 5
          },
          '&:hover': { color: darkPalette.common.white }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          background: 'transparent',
          transition: 'all .2s',
          '&:hover, &:active, &.active, &.Mui-selected': {
            color: darkPalette.common.white,
            background: alpha(darkPalette.primary.main, 0.16)
          }
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            color: darkPalette.secondary.main,
            '&:hover, &:active, &.active, &.Mui-selected': {
              color: darkPalette.common.white,
              background: alpha(darkPalette.primary.main, 0.1)
            }
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        tag: { margin: 1 },
        root: {
          '.MuiAutocomplete-inputRoot.MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
            right: 14
          }
        },
        clearIndicator: {
          background: alpha(darkPalette.error.main, 0.1),
          color: darkPalette.error.main,
          marginRight: 8,
          '&:hover': {
            background: alpha(darkPalette.error.main, 0.2),
            color: darkPalette.error.dark
          }
        },
        popupIndicator: {
          color: alpha(darkPalette.common.white, 0.5),
          '&:hover': {
            background: alpha(darkPalette.primary.main, 0.1),
            color: darkPalette.primary.main
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        head: { background: alpha(darkPalette.common.white, 0.05) },
        root: {
          transition: 'background-color .2s',
          '&.MuiTableRow-hover:hover': { backgroundColor: alpha(darkPalette.common.white, 0.05) }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        ...commonComponents.MuiTableCell.styleOverrides,
        root: {
          ...commonComponents.MuiTableCell.styleOverrides.root,
          borderBottomColor: alpha(darkPalette.common.white, 0.1)
        },
        head: {
          ...commonComponents.MuiTableCell.styleOverrides.head,
          color: alpha(darkPalette.common.white, 0.7)
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        message: { lineHeight: 1.5, fontSize: 14 },
        standardInfo: { color: darkPalette.info.main },
        action: { color: alpha(darkPalette.common.white, 0.7) }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(darkPalette.common.black, 0.95),
          padding: '8px 16px',
          fontSize: 13
        },
        arrow: {
          color: alpha(darkPalette.common.black, 0.95)
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          height: 33,
          overflow: 'visible',
          '& .MuiButtonBase-root': {
            position: 'absolute',
            padding: 6,
            transition: 'left 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
          },
          '& .MuiIconButton-root': { borderRadius: 100 },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { opacity: 0.3 }
        },
        thumb: {
          border: `1px solid ${alpha(darkPalette.common.white, 0.3)}`,
          boxShadow: `0px 9px 14px ${alpha(darkPalette.common.black, 0.3)}, 0px 2px 2px ${alpha(darkPalette.common.black, 0.3)}`
        },
        track: {
          backgroundColor: alpha(darkPalette.common.white, 0.05),
          border: `1px solid ${alpha(darkPalette.common.white, 0.1)}`,
          boxShadow: `inset 0px 1px 1px ${alpha(darkPalette.common.white, 0.1)}`,
          opacity: 1
        },
        colorPrimary: {
          '& .MuiSwitch-thumb': { backgroundColor: darkPalette.common.white },
          '&.Mui-checked .MuiSwitch-thumb': { backgroundColor: darkPalette.primary.main }
        }
      }
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          paddingTop: 20,
          paddingBottom: 20,
          background: alpha(darkPalette.common.white, 0.05)
        }
      }
    }
  },
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: { fontWeight: 700, fontSize: 35, color: darkPalette.text.primary },
    h2: { fontWeight: 700, fontSize: 30, color: darkPalette.text.primary },
    h3: { fontWeight: 700, fontSize: 25, lineHeight: 1.4, color: darkPalette.text.primary },
    h4: { fontWeight: 700, fontSize: 16, color: darkPalette.text.primary },
    h5: { fontWeight: 700, fontSize: 14, color: darkPalette.text.primary },
    h6: { fontSize: 15, color: darkPalette.text.primary },
    body1: { fontSize: 14, color: darkPalette.text.primary },
    body2: { fontSize: 14, color: darkPalette.text.secondary },
    button: { fontWeight: 600, color: darkPalette.text.primary },
    caption: { fontSize: 13, textTransform: 'uppercase', color: alpha(darkPalette.common.white, 0.5) },
    subtitle1: { fontSize: 14, color: alpha(darkPalette.common.white, 0.7) },
    subtitle2: { fontWeight: 400, fontSize: 15, color: alpha(darkPalette.common.white, 0.7) },
    overline: { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: darkPalette.text.primary }
  },
  shadows: [
    'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none',
    'none', 'none', 'none', 'none', 'none'
  ]
});